import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { apiErrorSlug, isApiErrorLike, isNetworkError } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { EmptyState } from "../primitives/EmptyState";
import { CuePill } from "../primitives/CuePill";
import {
  isLocalBellRow,
  type BellItem,
  type LocalActionId,
  type LocalBell,
} from "./bellRows";
import { InboxCard, type InboxCardAction } from "./InboxCard";
import { displayParams } from "./inboxParams";
import type { TFn } from "../i18nTypes";
import { useInboxContext, type InboxContextValue } from "./InboxProvider";
import type { InboxItem } from "./types";

// Slugs an accept/reject/dismiss can come back with and that the reader can
// act on. The other server slugs (io_error, schema_drift, a transport
// failure with no slug at all) say nothing a reader can use, so they share
// the generic "try again".
const ACTION_ERROR_KEYS: Record<string, string> = {
  item_gone: "inbox.actionErrorItemGone",
  no_longer_assigned: "inbox.actionErrorNoLongerAssigned",
  wrong_party: "inbox.actionErrorWrongParty",
  fanout_failed: "inbox.actionErrorFanout",
};

// An answer is online-only (offline-writes.md): with no connection the
// reader is told so. Offline, apiFetch can also fail as a 401 with no body
// (no token refresh, LG-31).
const isOffline = (err: Error): boolean =>
  isNetworkError(err) || (isApiErrorLike(err) && err.status === 401 && err.body == null);

function actionErrorKey(err: Error): string {
  if (isOffline(err)) return "inbox.actionErrorOffline";
  // Too many in a row (the phone's Send to support, #288, and any action
  // the server rate-limits): the feedback form's own words.
  if (isApiErrorLike(err) && err.status === 429) return "feedback.errRateLimit";
  const slug = apiErrorSlug(err);
  return (slug && ACTION_ERROR_KEYS[slug]) || "inbox.actionError";
}

export type InboxSectionProps = {
  theme: UiTheme;
  /** Called once a row's "Open" or "Details" has taken the reader to
   *  another page or sheet: the bell closes its panel. */
  onLeave?: () => void;
};

// #182: a raise is Confirm / Skip; a lower is Keep / Lower to / Bad day,
// the safe answer first. "Bad day" keeps the MAX and quiets this exercise
// until the drop recurs, which its hint says.
function maxBumpActions(
  item: InboxItem,
  t: TFn,
  inbox: Pick<InboxContextValue, "respondToProposal" | "formatParams">,
): InboxCardAction[] {
  const params = displayParams(item.params, t, inbox.formatParams);
  const answer = (decision: "accept" | "reject" | "snooze") => () =>
    void inbox.respondToProposal(item, decision);
  if (item.params?.direction === "lower") {
    return [
      { label: t("inbox.maxBump.keep", params), onPress: answer("reject") },
      { label: t("inbox.maxBump.lowerTo", params), onPress: answer("accept") },
      {
        label: t("inbox.maxBump.badDay"),
        onPress: answer("snooze"),
        hint: t("inbox.maxBump.badDayHint"),
      },
    ];
  }
  return [
    { label: t("inbox.maxBump.confirm"), onPress: answer("accept") },
    { label: t("inbox.maxBump.skip"), onPress: answer("reject") },
  ];
}

// Each origin maps its own actions; there is no generic fallback (#294).
function serverActions(
  item: InboxItem,
  t: TFn,
  inbox: Pick<
    InboxContextValue,
    "respondToProposal" | "dismissNotice" | "formatParams" | "openTarget"
  >,
  onLeave?: () => void,
): InboxCardAction[] {
  if (item.kind === "proposal" && item.refType === "maxBump") {
    return maxBumpActions(item, t, inbox);
  }
  if (item.kind === "proposal") {
    return [
      { label: t("inbox.accept"), onPress: () => void inbox.respondToProposal(item, "accept") },
      {
        label: t("inbox.decline"),
        onPress: () => void inbox.respondToProposal(item, "reject"),
        destructive: true,
      },
    ];
  }
  const dismiss: InboxCardAction = {
    label: t("inbox.acknowledge"),
    onPress: () => void inbox.dismissNotice(item),
  };
  // The missing bodyweight (#296): "Add weight" opens Me → Measurements, and
  // the dismiss is permanent, so it says so ("Don't remind me").
  if (item.refType === "bodyweight") {
    const openTarget = inbox.openTarget;
    const forever: InboxCardAction = {
      ...dismiss,
      label: t("inbox.bodyweight.missing.dismiss"),
    };
    if (!openTarget) return [forever];
    return [
      {
        label: t("inbox.bodyweight.missing.cta"),
        onPress: () => {
          if (openTarget({ to: "settingsMeasurements" })) onLeave?.();
        },
      },
      forever,
    ];
  }
  return [dismiss];
}

// "Close" acknowledges a phone notice as "Got it" does a server one. "Save
// as new" keeps the recovery card's words: the server row other devices show
// for a lost correction tells the athlete to tap exactly that (#288).
const LOCAL_ACTION_LABEL: Record<LocalActionId, string> = {
  open: "inbox.local.action.open",
  hide: "inbox.local.action.hide",
  close: "inbox.acknowledge",
  retry: "inbox.local.action.retry",
  details: "inbox.local.action.details",
  resume: "inbox.local.action.resume",
  discard: "inbox.local.action.discard",
  saveAsNew: "mobile.recovery.saveAsNew",
  merge: "inbox.local.action.merge",
  sendSupport: "inbox.local.action.sendSupport",
};

/** What a running action says instead of its label. */
const LOCAL_ACTION_BUSY_LABEL: Partial<Record<LocalActionId, string>> = {
  retry: "inbox.local.action.retrying",
  sendSupport: "inbox.local.action.sending",
};

/** These take the reader away from the panel, so the bell closes it. */
const LEAVES: ReadonlySet<LocalActionId> = new Set([
  "open",
  "details",
  "resume",
  "saveAsNew",
  "merge",
]);

/** Words first, the danger tone beside them. */
const DESTRUCTIVE: ReadonlySet<LocalActionId> = new Set(["discard"]);

function actionsOf(
  item: BellItem,
  t: TFn,
  inbox: InboxContextValue,
  onLeave: (() => void) | undefined,
): InboxCardAction[] {
  if (!isLocalBellRow(item)) return serverActions(item, t, inbox, onLeave);
  const local = inbox.local;
  if (!local) return [];
  return item.actions.map((id) => {
    const busyKey = LOCAL_ACTION_BUSY_LABEL[id];
    return {
      label: t(LOCAL_ACTION_LABEL[id]),
      ...(busyKey ? { busyLabel: t(busyKey) } : {}),
      ...(DESTRUCTIVE.has(id) ? { destructive: true } : {}),
      onPress: () =>
        void local.run(item, id).then((done) => {
          if (done && LEAVES.has(id)) onLeave?.();
        }),
    };
  });
}

const busyOf = (item: BellItem, busyId: string | null, local?: LocalBell) =>
  isLocalBellRow(item) ? local?.busyId === item.id : busyId === item.id;

/**
 * The inbox list (SPEC, fetta 1), drawn in the bell's panel (INBOX-10).
 * Since #295 the bell is its only place: the Home widget is gone. Reads
 * `useInboxContext()`, the ONE `useInbox` call an `InboxProvider` mounted
 * near the shell root owns, rather than fetching on its own (the SPEC's
 * "una sola fetch" rule, mirroring CN-coach-edges-fetch).
 */
export function InboxSection({ theme, onLeave }: InboxSectionProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const inbox = useInboxContext();
  const { items, status, busyId, refresh, local } = inbox;
  const hasItems = !!items && items.length > 0;

  // Still loading (nothing cached yet): reserve the row's usual footprint
  // instead of popping in later and shoving down whatever the athlete is
  // already reading below it (a real answer — even the empty one — always
  // clears this in one round trip). The phone's rows need no round trip.
  if ((status === "idle" || status === "loading") && !hasItems) {
    return <View style={styles.loadingSpace} />;
  }
  // The read failed and nothing is cached: say so, with a way to retry. An
  // empty return here left the bell's panel blank under its title.
  const loadError =
    status === "error" && !(items ?? []).some((it) => !isLocalBellRow(it)) ? (
      <CuePill
        theme={theme}
        tone="danger"
        icon="warn"
        size="base"
        weight="600"
        live
        style={styles.banner}
        label={t("inbox.loadError")}
        action={{ label: t("inbox.retry"), onPress: () => void refresh() }}
      />
    ) : null;
  if (loadError && !hasItems) return loadError;
  const actionError = inbox.actionError ?? local?.error ?? null;

  // One write in flight can fail without touching `items` at all (a
  // dismiss/accept/reject that 4xx'd for a reason other than "already
  // resolved", useInbox.ts's respondToProposal/dismissNotice). Surfaced
  // above the list, not silently dropped: mai-solo-colore, so the icon +
  // words carry it, the danger tint only reinforces.
  // INBOX-7(b): every known slug has its own words, and anything else gets
  // the generic line. A raw slug is never printed.
  const errorBanner = actionError ? (
    <CuePill
      theme={theme}
      tone="danger"
      icon="warn"
      size="base"
      weight="600"
      live
      style={styles.banner}
      label={t(actionErrorKey(actionError))}
    />
  ) : null;

  if (!items || items.length === 0) {
    return (
      <>
        {errorBanner}
        <EmptyState
          theme={theme}
          icon="inbox"
          title={t("inbox.emptyTitle")}
          sub={t("inbox.emptyBody")}
        />
      </>
    );
  }

  return (
    <View style={styles.list}>
      {loadError}
      {errorBanner}
      {items.map((item) => (
        <InboxCard
          key={item.id}
          theme={theme}
          item={item}
          busy={busyOf(item, busyId, local)}
          actions={actionsOf(item, t, inbox, onLeave)}
          formatParams={inbox.formatParams}
        />
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({
    list: { gap: theme.spacing.s3 },
    // Without an action or a dismiss CuePill hugs its content and sets no
    // maxWidth; this message is a long sentence in all five locales.
    banner: { alignSelf: "stretch" },
    // Rough stand-in for the EmptyState footprint (icon + title line + sub
    // line + its s6 top/bottom padding) — not exact for every locale, but
    // close enough that the loading→answer swap stops being a full-height
    // pop-in for whoever is already reading past this row.
    loadingSpace: { minHeight: 96 },
  });
}
